import type {
  BpOrder,
  DeliveryAddressInput,
  GetDoorDashQuoteParams,
  GetUberDirectQuoteParams,
  SimpleNumberSelectOption,
} from '~/definitions/AllDefinitions'
import { useMainStore } from '~/store/MainStore'
import { useUserStore } from '~/store/UserStore'
import { defaultError } from '~/classes/ErrorHandlers'
import { DELIVERY_METHODS, HTTP_METHODS } from '~/definitions/enums'
import type { OrderStoreInterface } from '~/definitions/StateDefinitions'
import { getFormattedAddress } from '~/classes/Utils'

const defaultOrder = {
  addressOne: null,
  addressTwo: null,
  addressThree: null,
  city: null,
  state: null,
  postalCode: null,
  country: 'US',
  latitude: 0,
  longitude: 0,
  email: '',
  firstName: null,
  lastName: null,
  phoneCountry: 'us',
  phoneFormatted: null,
  phoneUnformatted: null,
  phoneType: 'unknown',
  sublocality: null,
  id: 0,
  accountId: 0,
  locationId: 0,
  cartId: null,
  userId: null,
  acceptedAt: null,
  completionDate: null,
  contactFreeDelivery: false,
  curbsidePickup: false,
  deliveryFormattedAddress: '',
  dropoffInstructions: null,
  deliveryMethod: 'pickup',
  deliveryTotal: 0,
  discountCode: '',
  discountTotal: 0,
  dispatchedAt: null,
  enableEmailNotifications: true,
  enableSmsNotifications: true,
  eta: null,
  exceptionDescription: null,
  gratuityTotal: 0,
  isManual: false,
  isRead: false,
  isTest: false,
  merchantNotes: null,
  orderNumber: 0,
  paymentCardLastFour: null,
  paymentCardType: null,
  paymentMethod: 'card',
  prepTime: 0,
  rejectDescription: null,
  rejectType: null,
  scheduledTime: { label: '', value: 0 },
  status: '',
  stripeTransactionId: null,
  subTotal: 0,
  taxRate: 0,
  taxTotal: 0,
  total: 0,
  trackingNumber: null,
  items: [],
  prettyCreatedAt: null,
  scheduled: false,
  unread: false,
  scheduledDay: { label: '', value: 0 },
  contactlessDelivery: false,
} as BpOrder
export const useOrderStore = defineStore('order-store', {
  persist: {
    pick: ['order', 'curbsidePickup'],
  },
  state: (): OrderStoreInterface => ({
    order: { ...defaultOrder },
    orderUuid: '',
    orderLocationUuid: '',
    orderId: 0,
    curbsidePickup: false,
  }),
  getters: {
    currentOrder: state => state.order,
    formattedDeliveryAddress: (state) => {
      return getFormattedAddress(state.order)
    },
    isDelivery: state => state.order.deliveryMethod === DELIVERY_METHODS.DELIVERY,
  },
  actions: {
    clear() {
      this.order = {
        ...defaultOrder,
      } as BpOrder
      this.orderUuid = ''
      this.orderLocationUuid = ''
      this.orderId = 0
      this.curbsidePickup = false
    },
    apiDetails(options?: { method?: string } | Record<any, any>) {
      const mainStore = useMainStore()
      if (!(mainStore.accountId && mainStore.currentLocation && mainStore.currentLocation.id)) {
        return null
      }
      const ret = {
        locationUrl: `/api/${mainStore.accountId}/locations/${mainStore.currentLocation.id}`,
        url: `/api/${mainStore.accountId}/locations/${mainStore.currentLocation.id}/subscriber-orders`,
        options: {},
      } as any
      if (options) {
        ret.options = { ...options }
      }
      return ret
    },
    clearDiscount() {
      this.order.discountCode = null
      this.order.discountTotal = 0
    },
    async createOrder(body: any) {
      const apiDetails = this.apiDetails({
        method: HTTP_METHODS.POST,
        body,
      })
      if (!apiDetails) {
        return defaultError
      }
      try {
        const data = await $fetch(apiDetails.url, apiDetails.options)
        return {
          error: null,
          data,
          success: true,
        }
      } catch (err: any) {
        const errorData = err?.data ?? err?.response?._data
        const mainStore = useMainStore()
        const formatError = useFormatError()
        if (errorData) {
          const { code, location } = errorData
          if (code === 'LOCATION_CLOSED') {
            mainStore.currentLocation = location
          } else {
            await mainStore.getLocation(mainStore.currentLocation!.id)
          }
        } else {
          await mainStore.getLocation(mainStore.currentLocation!.id)
        }
        return {
          error: formatError(err),
          data: errorData || null,
          success: false,
        }
      }
    },
    async getDoorDashQuote(body: GetDoorDashQuoteParams) {
      const apiDetails = this.apiDetails({
        method: HTTP_METHODS.POST,
        body,
      })
      if (!apiDetails) {
        return defaultError
      }
      try {
        const url = `${apiDetails.locationUrl}/delivery-quotes/doordash`
        const data = await $fetch(url, apiDetails.options)
        return { success: true, data, error: null }
      } catch (err: any) {
        const formatError = useFormatError()
        return { success: false, data: null, error: formatError(err) }
      }
    },
    async getOrder(locationIdentifier: string, orderUuid: string, email: string) {
      const mainStore = useMainStore()
      if (!mainStore.accountId) {
        this.clear()
        return
      }
      const config = {
        query: {
          email,
        },
      } as any
      try {
        const data = await $fetch(
          `/api/${mainStore.accountId}/locations/subscriber-orders/${locationIdentifier}/${orderUuid}`,
          config,
        )
        if (data) {
          this.order = data as BpOrder
          if (this.order.uuid) {
            this.orderUuid = this.order.uuid
          }
          this.orderId = this.order.id
          this.orderLocationUuid = locationIdentifier
        } else {
          this.clear()
        }
      } catch {
        this.clear()
      }
    },
    async getOrders() {
      const mainStore = useMainStore()
      const userStore = useUserStore()
      if (!(mainStore.accountId && userStore.currentUser)) {
        return defaultError
      }
      try {
        const data = await $fetch(`/api/${mainStore.accountId}/users/me/orders`, {
          query: {
            orderBy: 'created_at',
            orderDirection: 'desc',
          },
        })
        return { success: true, data, error: null }
      } catch (err: any) {
        const formatError = useFormatError()
        return { success: false, data: null, error: formatError(err) }
      }
    },
    async getUberDirectQuote(body: GetUberDirectQuoteParams) {
      const apiDetails = this.apiDetails({
        method: HTTP_METHODS.POST,
        body,
      })
      if (!apiDetails) {
        return defaultError
      }
      try {
        const url = `${apiDetails.locationUrl}/delivery-quotes/uber-direct`
        const data = await $fetch(url, apiDetails.options)
        return { success: true, data, error: null }
      } catch (err: any) {
        const formatError = useFormatError()
        return { success: false, data: null, error: formatError(err) }
      }
    },
    getUberFormattedAddress(input: Record<any, any>) {
      return {
        street_address: [input.addressOne ?? ''],
        city: input.city ?? '',
        country: input.country ?? '',
        state: input.state ?? '',
        zip_code: input.postalCode ?? '',
      } as DeliveryAddressInput
    },
    async getDriverLocation() {
      const mainStore = useMainStore()
      if (!mainStore.accountId || !this.orderLocationUuid || !this.orderUuid) {
        return null
      }
      try {
        const base = `/api/${mainStore.accountId}/locations/subscriber-orders`
        const data = await $fetch(
          `${base}/${this.orderLocationUuid}/${this.orderUuid}/driver-location`,
          { query: { email: this.order.email || '' } },
        )
        return data
      } catch {
        return null
      }
    },
    async updateDeliveryInstructions(dropoffInstructions: string) {
      const mainStore = useMainStore()
      if (!mainStore.accountId || !this.orderLocationUuid || !this.orderUuid) {
        return { error: true, message: 'Missing order details' }
      }
      try {
        await $fetch(
          `/api/${mainStore.accountId}/locations/subscriber-orders/${this.orderLocationUuid}/${this.orderUuid}`,
          {
            method: 'PATCH',
            body: { dropoffInstructions },
            query: { email: this.order.email || '' },
          },
        )
        if (this.order?.deliveryDetails) {
          this.order.deliveryDetails.dropoffInstructions = dropoffInstructions
        }
        return { error: false }
      } catch (e: any) {
        const formatError = useFormatError()
        return { error: true, message: formatError(e) }
      }
    },
    setCurbsidePickup(curbsidePickup: boolean) {
      this.curbsidePickup = curbsidePickup
    },
    setScheduledDay(day: SimpleNumberSelectOption) {
      this.order.scheduledDay = day
    },
    setScheduledTime(time: SimpleNumberSelectOption) {
      this.order.scheduledTime = time
    },
  },
})
